import { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { TbTrendingUp } from "react-icons/tb";
import { useColorMode } from "../../../../Hooks/useColorMode";
import { formatCrossborderAmount, MOCK_PAYOUT_VOLUME } from "../constants";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

const buildChartOptions = (isDark) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: isDark ? "#333" : "#242424",
      titleFont: { size: 12, weight: "600" },
      bodyFont: { size: 12 },
      padding: 10,
      cornerRadius: 8,
      displayColors: false,
      callbacks: {
        label: (context) =>
          formatCrossborderAmount(context.parsed.y, MOCK_PAYOUT_VOLUME.currencyId),
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(229, 231, 235, 0.6)",
        drawBorder: false,
      },
      ticks: {
        color: isDark ? "#6b7280" : "#9ca3af",
        font: { size: 11 },
        maxTicksLimit: 7,
      },
      border: { display: false },
    },
    y: {
      display: false,
      beginAtZero: true,
      grid: { display: false },
    },
  },
  elements: {
    line: {
      tension: 0.35,
      borderWidth: 2,
    },
    point: {
      radius: 0,
      hoverRadius: 4,
      hitRadius: 12,
    },
  },
});

export const CrossborderPayoutVolumeCard = ({ className = "" }) => {
  const { isDark } = useColorMode();
  const { changePercent, todayTotal, yesterdayTotal, currencyId } = MOCK_PAYOUT_VOLUME;
  const isPositive = changePercent >= 0;

  const chartOptions = useMemo(() => buildChartOptions(isDark), [isDark]);

  const chartData = useMemo(
    () => ({
      labels: MOCK_PAYOUT_VOLUME.hourlyLabels,
      datasets: [
        {
          label: "Hoje",
          data: MOCK_PAYOUT_VOLUME.hourlyValues,
          borderColor: "#e7a73f",
          backgroundColor: "rgba(231, 167, 63, 0.12)",
          fill: true,
          pointBackgroundColor: "#e7a73f",
        },
        {
          label: "Ontem",
          data: MOCK_PAYOUT_VOLUME.yesterdayHourlyValues,
          borderColor: isDark ? "#555" : "#d1d5db",
          backgroundColor: "transparent",
          fill: false,
          borderDash: [4, 4],
          pointBackgroundColor: isDark ? "#555" : "#d1d5db",
        },
      ],
    }),
    [isDark],
  );

  return (
    <section className={`app-card overflow-hidden ${className}`.trim()}>
      <div className="border-b border-gray-100 px-4 py-4 dark:border-brand-dark-border sm:px-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Volume hoje
            </p>
            <p className="mt-1 text-2xl font-bold text-brand-dark dark:text-gray-100 sm:text-3xl">
              {formatCrossborderAmount(todayTotal, currencyId)}
            </p>
          </div>
          <div
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
              isPositive
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                : "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300"
            }`}
          >
            <TbTrendingUp className={`h-3.5 w-3.5 ${isPositive ? "" : "rotate-180"}`} />
            {isPositive ? "+" : ""}
            {changePercent.toFixed(1)}% vs ontem
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Ontem: {formatCrossborderAmount(yesterdayTotal, currencyId)} em payouts crossborder
        </p>
      </div>

      <div className="px-2 pb-3 pt-2 sm:px-4">
        <div className="mb-2 flex items-center gap-4 px-2 text-xs text-gray-500 dark:text-gray-400">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-4 rounded bg-[#e7a73f]" />
            Hoje
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0 w-4 border-t border-dashed border-gray-300 dark:border-gray-600" />
            Ontem
          </span>
        </div>
        <div className="h-44 w-full sm:h-48">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>
    </section>
  );
};
